import { useEffect, useState, useRef } from "react"
import { Form, Alert, Spinner, Container, Row, Col, ProgressBar } from "react-bootstrap"
import { useLocation } from "react-router-dom"
import { Button, icon, variant } from "../form/Button"

export interface BulkMediaUploadProps {
    onFileUpload: (key: string, file: File, options?: { contentType: string }) => Promise<any>
}

interface UploadFile {
    id: string
    file: File
    preview: string
    status: 'pending' | 'uploading' | 'success' | 'error'
    progress: number
    error?: string
}

interface UploadSummary {
    total: number
    successful: number
    failed: number
}

const BulkMediaUpload: React.FC<BulkMediaUploadProps> = (props) => {
    const [shopId, setShopId] = useState<string | null>(null)
    const [files, setFiles] = useState<UploadFile[]>([])
    const [isUploading, setIsUploading] = useState(false)
    const [error, setError] = useState("")
    const [success, setSuccess] = useState("")
    const [uploadSummary, setUploadSummary] = useState<UploadSummary | null>(null)
    const [isDragOver, setIsDragOver] = useState(false)

    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const location = useLocation()

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search)
        const shopIdParam = queryParams.get("shopid")

        if (!shopIdParam) {
            setError("Shop ID is required in the URL parameters")
            return
        }

        setShopId(shopIdParam)
        setError("")
    }, [location.search])

    // Drag and drop event handlers
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(true)
    }

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)

        const droppedFiles = Array.from(e.dataTransfer.files)
        if (droppedFiles.length > 0) {
            processFiles(droppedFiles)
        }
    }

    const handleDragZoneClick = () => {
        fileInputRef.current?.click()
    }

    // File validation constants
    const MAX_FILE_SIZE_IMAGE = 10 * 1024 * 1024 // 10MB
    const MAX_FILE_SIZE_VIDEO = 50 * 1024 * 1024 // 50MB
    const MAX_FILES = 20 // Maximum number of files
    const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/mov', 'video/avi', 'video/webm']

    const validateFiles = (fileList: File[]): { validFiles: File[], errors: string[] } => {
        const validFiles: File[] = []
        const errors: string[] = []

        // Check total number of files
        if (fileList.length > MAX_FILES) {
            errors.push(`Maximum ${MAX_FILES} files allowed. You selected ${fileList.length} files.`)
            return { validFiles, errors }
        }

        fileList.forEach((file, index) => {
            const isImage = ALLOWED_IMAGE_TYPES.includes(file.type)
            const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type)

            // Check file type
            if (!isImage && !isVideo) {
                errors.push(`File "${file.name}": Unsupported file type. Only images (JPG, PNG, GIF, WebP) and videos (MP4, MOV, AVI, WebM) are allowed.`)
                return
            }

            // Check file size
            const maxSize = isImage ? MAX_FILE_SIZE_IMAGE : MAX_FILE_SIZE_VIDEO
            if (file.size > maxSize) {
                const maxSizeMB = maxSize / (1024 * 1024)
                errors.push(`File "${file.name}": File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds the ${maxSizeMB}MB limit for ${isImage ? 'images' : 'videos'}.`)
                return
            }

            validFiles.push(file)
        })

        return { validFiles, errors }
    }

    const generateFilePreview = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const isImage = ALLOWED_IMAGE_TYPES.includes(file.type)

            if (isImage) {
                const reader = new FileReader()
                reader.onload = (e) => {
                    resolve(e.target?.result as string)
                }
                reader.onerror = () => {
                    reject(new Error(`Failed to generate preview for ${file.name}`))
                }
                reader.readAsDataURL(file)
            } else {
                // For videos, use a generic video icon
                resolve("video-icon")
            }
        })
    }

    const processFiles = async (fileList: File[]) => {
        const { validFiles, errors } = validateFiles(fileList)

        if (errors.length > 0) {
            setError(errors.join(' '))
            return
        }

        if (validFiles.length > 0) {
            setError("")

            // Generate previews and create UploadFile objects
            const newUploadFiles: UploadFile[] = []

            for (const file of validFiles) {
                try {
                    const preview = await generateFilePreview(file)
                    const uploadFile: UploadFile = {
                        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        file,
                        preview,
                        status: 'pending',
                        progress: 0
                    }
                    newUploadFiles.push(uploadFile)
                } catch (error) {
                    console.error(`Failed to generate preview for ${file.name}:`, error)
                    // Still add the file but with a fallback preview
                    const uploadFile: UploadFile = {
                        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        file,
                        preview: "error",
                        status: 'pending',
                        progress: 0
                    }
                    newUploadFiles.push(uploadFile)
                }
            }

            // Add new files to existing files array
            setFiles(prevFiles => [...prevFiles, ...newUploadFiles])
        }
    }

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(e.target.files || [])
        if (selectedFiles.length > 0) {
            processFiles(selectedFiles)
        }
        // Reset the input value to allow selecting the same files again
        e.target.value = ""
    }

    const removeFile = (fileId: string) => {
        setFiles(prevFiles => prevFiles.filter(file => file.id !== fileId))
        setError("") // Clear any errors when files are removed
    }

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const getQueueSummary = () => {
        const totalSize = files.reduce((sum, file) => sum + file.file.size, 0)
        const statusCounts = files.reduce((counts, file) => {
            counts[file.status] = (counts[file.status] || 0) + 1
            return counts
        }, {} as Record<string, number>)

        return {
            totalFiles: files.length,
            totalSize,
            pending: statusCounts.pending || 0,
            uploading: statusCounts.uploading || 0,
            success: statusCounts.success || 0,
            error: statusCounts.error || 0
        }
    }

    // Upload state management
    const MAX_CONCURRENT_UPLOADS = 3
    const [activeUploads, setActiveUploads] = useState<Set<string>>(new Set())

    const updateFileStatus = (fileId: string, status: UploadFile['status'], progress?: number, error?: string) => {
        setFiles(prevFiles =>
            prevFiles.map(file =>
                file.id === fileId
                    ? { ...file, status, progress: progress ?? file.progress, error }
                    : file
            )
        )
    }

    const canStartUpload = () => {
        return activeUploads.size < MAX_CONCURRENT_UPLOADS
    }

    const addActiveUpload = (fileId: string) => {
        setActiveUploads(prev => new Set(Array.from(prev).concat(fileId)))
    }

    const removeActiveUpload = (fileId: string) => {
        setActiveUploads(prev => {
            const newSet = new Set(prev)
            newSet.delete(fileId)
            return newSet
        })
    }

    // Overall progress calculation
    const getOverallProgress = () => {
        if (files.length === 0) return 0

        const totalProgress = files.reduce((sum, file) => {
            switch (file.status) {
                case 'success': return sum + 100
                case 'uploading': return sum + file.progress
                case 'error': return sum + 0
                case 'pending': return sum + 0
                default: return sum
            }
        }, 0)

        return Math.round(totalProgress / files.length)
    }

    // Upload execution logic
    const uploadSingleFile = async (uploadFile: UploadFile): Promise<void> => {
        if (!shopId) {
            throw new Error("Shop ID is required for upload")
        }

        const timestamp = Date.now()
        const key = `${shopId}/media/${timestamp}-${uploadFile.file.name}`

        try {
            addActiveUpload(uploadFile.id)
            updateFileStatus(uploadFile.id, 'uploading', 0)

            // Simulate progress updates (since the actual upload might not provide progress)
            const progressInterval = setInterval(() => {
                updateFileStatus(uploadFile.id, 'uploading', Math.min(90, Math.random() * 80 + 10))
            }, 500)

            await props.onFileUpload(key, uploadFile.file, {
                contentType: uploadFile.file.type
            })

            clearInterval(progressInterval)
            updateFileStatus(uploadFile.id, 'success', 100)

        } catch (error) {
            updateFileStatus(uploadFile.id, 'error', 0, error instanceof Error ? error.message : 'Upload failed')
            throw error
        } finally {
            removeActiveUpload(uploadFile.id)
        }
    }

    const uploadAllFiles = async () => {
        const pendingFiles = files.filter(file => file.status === 'pending')

        if (pendingFiles.length === 0) {
            return
        }

        setIsUploading(true)
        setError("")

        const uploadPromises: Promise<void>[] = []
        let uploadIndex = 0

        const processNextUpload = async () => {
            while (uploadIndex < pendingFiles.length && canStartUpload()) {
                const file = pendingFiles[uploadIndex++]
                const uploadPromise = uploadSingleFile(file)
                    .catch(error => {
                        console.error(`Upload failed for ${file.file.name}:`, error)
                    })
                    .finally(() => {
                        // Process next upload when this one completes
                        processNextUpload()
                    })

                uploadPromises.push(uploadPromise)
            }
        }

        // Start initial uploads
        await processNextUpload()

        // Wait for all uploads to complete
        await Promise.allSettled(uploadPromises)

        setIsUploading(false)

        // Generate upload summary
        const summary = getQueueSummary()
        setUploadSummary({
            total: summary.totalFiles,
            successful: summary.success,
            failed: summary.error
        })

        if (summary.success > 0) {
            setSuccess(`Successfully uploaded ${summary.success} file${summary.success > 1 ? 's' : ''}${summary.error > 0 ? ` (${summary.error} failed)` : ''}`)
        }
    }

    const retryFailedFile = async (fileId: string) => {
        const file = files.find(f => f.id === fileId)
        if (!file || file.status !== 'error') {
            return
        }

        updateFileStatus(fileId, 'pending', 0, undefined)

        try {
            await uploadSingleFile(file)
        } catch (error) {
            console.error(`Retry failed for ${file.file.name}:`, error)
        }
    }

    const renderFilePreview = (uploadFile: UploadFile) => {
        const isImage = ALLOWED_IMAGE_TYPES.includes(uploadFile.file.type)

        if (isImage && uploadFile.preview !== "error") {
            return (
                <img
                    src={uploadFile.preview}
                    alt={uploadFile.file.name}
                    style={{
                        width: "60px",
                        height: "60px",
                        objectFit: "cover",
                        borderRadius: "4px"
                    }}
                />
            )
        } else {
            // Video or error fallback
            return (
                <div style={{
                    width: "60px",
                    height: "60px",
                    backgroundColor: "#e9ecef",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px"
                }}>
                    {uploadFile.preview === "error" ? "❌" : "🎥"}
                </div>
            )
        }
    }

    // Placeholder render - will be implemented in subsequent tasks
    if (!shopId && error) {
        return (
            <Container className="my-4">
                <Alert variant="danger">
                    {error}
                </Alert>
            </Container>
        )
    }

    if (!shopId) {
        return (
            <Container className="my-4">
                <div style={{ textAlign: "center" }}>
                    <Spinner variant="primary" animation="border" />
                </div>
            </Container>
        )
    }

    return (
        <Container className="my-4">
            <Alert variant="danger" show={error !== ""}>
                {error}
            </Alert>
            <Alert variant="success" show={success !== ""}>
                {success}
            </Alert>

            {/* Drag and Drop Zone */}
            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleDragZoneClick}
                className="text-center"
                style={{
                    border: `2px dashed ${isDragOver ? "#007bff" : "#ced4da"}`,
                    borderRadius: "0.25rem",
                    padding: "2rem 1rem",
                    backgroundColor: isDragOver ? "#e3f2fd" : "#f8f9fa",
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                    minHeight: "180px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    touchAction: "manipulation"
                }}
            >
                <div style={{ fontSize: "2.5rem", color: "#6c757d", marginBottom: "1rem" }}>
                    📁
                </div>
                <h5 className="h6 h5-sm" style={{ color: "#495057", marginBottom: "0.5rem" }}>
                    {isDragOver ? "Drop files here" : "Drag and drop files here"}
                </h5>
                <p style={{ color: "#6c757d", marginBottom: "1rem", fontSize: "14px" }}>
                    or tap to browse files
                </p>
                <small style={{ color: "#6c757d", fontSize: "12px", lineHeight: "1.4" }}>
                    Supports images (JPG, PNG, GIF, WebP) and videos (MP4, MOV, AVI, WebM)
                </small>
            </div>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/mov,video/avi,video/webm"
                style={{ display: "none" }}
                onChange={handleFileInputChange}
            />

            {/* File Queue Display */}
            {files.length > 0 && (
                <div className="mt-4">
                    <Row className="mb-3">
                        <Col>
                            <h6 className="mb-2">Selected Files ({files.length})</h6>
                            <div style={{ fontSize: "14px", color: "#6c757d" }}>
                                Total size: {formatFileSize(getQueueSummary().totalSize)}
                                {getQueueSummary().uploading > 0 && (
                                    <span className="ms-2">• {getQueueSummary().uploading} uploading</span>
                                )}
                                {getQueueSummary().success > 0 && (
                                    <span className="ms-2 text-success">• {getQueueSummary().success} completed</span>
                                )}
                                {getQueueSummary().error > 0 && (
                                    <span className="ms-2 text-danger">• {getQueueSummary().error} failed</span>
                                )}
                            </div>
                        </Col>
                        <Col xs="auto" style={{ display: "flex", gap: "8px" }}>
                            {getQueueSummary().pending > 0 && !isUploading && (
                                <Button
                                    buttonvariant={variant.Primary}
                                    name="Upload All"
                                    icon={isUploading ? icon.Spinner : undefined}
                                    disabled={isUploading}
                                    onClick={uploadAllFiles}
                                />
                            )}
                            {getQueueSummary().error > 0 && !isUploading && (
                                <Button
                                    buttonvariant={variant.Secondary}
                                    name="Retry Failed"
                                    onClick={() => {
                                        const failedFiles = files.filter(f => f.status === 'error')
                                        failedFiles.forEach(file => retryFailedFile(file.id))
                                    }}
                                />
                            )}
                        </Col>
                    </Row>

                    {/* Overall Progress Bar */}
                    {(isUploading || getQueueSummary().success > 0 || getQueueSummary().error > 0) && (
                        <div className="mb-3">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                                <small style={{ color: "#6c757d" }}>Overall Progress</small>
                                <small style={{ color: "#6c757d" }}>{getOverallProgress()}%</small>
                            </div>
                            <ProgressBar
                                now={getOverallProgress()}
                                variant={getQueueSummary().error > 0 ? "warning" : "success"}
                                style={{ height: "8px" }}
                            />
                        </div>
                    )}
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                        {files.map((uploadFile) => (
                            <div
                                key={uploadFile.id}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "12px",
                                    border: "1px solid #dee2e6",
                                    borderRadius: "4px",
                                    marginBottom: "8px",
                                    backgroundColor: "#fff",
                                    flexWrap: "wrap",
                                    gap: "8px"
                                }}
                                className="d-flex flex-column flex-sm-row"
                            >
                                {/* File Preview */}
                                <div style={{ marginRight: "12px" }}>
                                    {renderFilePreview(uploadFile)}
                                </div>

                                {/* File Information */}
                                <div className="flex-grow-1" style={{ minWidth: "200px" }}>
                                    <div style={{
                                        fontWeight: "500",
                                        fontSize: "14px",
                                        color: "#495057",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                    }}>
                                        {uploadFile.file.name}
                                    </div>
                                    <div style={{
                                        fontSize: "12px",
                                        color: "#6c757d",
                                        marginTop: "2px"
                                    }}>
                                        {formatFileSize(uploadFile.file.size)} • {uploadFile.file.type}
                                    </div>
                                    {uploadFile.status === 'uploading' && (
                                        <ProgressBar
                                            now={uploadFile.progress}
                                            className="mt-2"
                                            style={{ height: "4px" }}
                                        />
                                    )}
                                    {uploadFile.error && (
                                        <div style={{
                                            fontSize: "12px",
                                            color: "#dc3545",
                                            marginTop: "2px"
                                        }}>
                                            {uploadFile.error}
                                        </div>
                                    )}
                                </div>

                                {/* Status and Action Buttons */}
                                <div className="d-flex align-items-center" style={{ gap: "8px", minHeight: "44px" }}>
                                    {uploadFile.status === 'success' && (
                                        <span style={{ color: "#28a745", fontSize: "18px" }}>✓</span>
                                    )}
                                    {uploadFile.status === 'error' && (
                                        <>
                                            <span style={{ color: "#dc3545", fontSize: "18px" }}>X</span>
                                            <Button
                                                buttonvariant={variant.Secondary}
                                                name="Retry"
                                                onClick={() => retryFailedFile(uploadFile.id)}
                                                style={{ padding: "6px 12px", fontSize: "12px", minHeight: "32px" }}
                                            />
                                        </>
                                    )}
                                    {uploadFile.status === 'uploading' && (
                                        <Spinner animation="border" style={{ width: "20px", height: "20px" }} />
                                    )}
                                    <Button
                                        buttonvariant={variant.Danger}
                                        icon={icon.Trash}
                                        onClick={() => removeFile(uploadFile.id)}
                                        disabled={uploadFile.status === 'uploading'}
                                        style={{ padding: "8px", fontSize: "12px", minHeight: "32px", minWidth: "32px" }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Container>
    )
}

export default BulkMediaUpload